import { createContext, ReactNode, useContext, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';

interface Props {
    children: ReactNode;
    instance: PublicClientApplication;
}

const userDefault = {
    accessToken: '',
    expiresOn: null as Date | null,
    name: '' as string | undefined,
    roles: [] as string[],
    groups: [] as string[]
};

const AuthContext = createContext({ 
    loading: true,
    user: userDefault,
    acquireTokenSilently: () => new Promise<string | any>(resolve => resolve('')),
    forceLogout: () => {},
    getUser: () => {},
    login: () => {},
    logout: () => {},
    isAuthenticated: false as boolean,
});

export const AuthProvider = ({children, instance}: Props) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(userDefault);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const acquireTokenSilently = () => {
        return new Promise((resolve, reject) => {
            if(Number(user.expiresOn) > Number(Date.now())) {
                resolve(user.accessToken);
                return;
            }
            const account = instance.getAllAccounts()[0];
            const accessTokenRequest = {
                scopes: [`api://${process.env.REACT_APP_AZURE_CLIENT_ID}/.default`],
                account: account,
                forceRefresh: true
            };
            instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse) => {
                setUser({
                    accessToken: accessTokenResponse.accessToken,
                    expiresOn: accessTokenResponse.expiresOn,
                    name: accessTokenResponse.account?.name ?? '',
                    roles: accessTokenResponse.idTokenClaims['roles'],
                    groups: accessTokenResponse.idTokenClaims['groups'],
                })
                resolve(accessTokenResponse.accessToken);
                return;
            }).catch((e: any) => {
                console.log(e);
                setIsAuthenticated(false);
                setUser(userDefault);
                reject(userDefault.accessToken);
                return;
            });
        });
    };

    /**
     * Bug with @azure/msal-react, instance.logout() redirects the user to select which account to signout from instead of automatically logging out.
     * Issue for implementing an inactivity timer logout, URL directly logs the user out. 
     */
    const forceLogout = () => {
        localStorage.setItem('logout-event', 'logout' + Math.random());
        localStorage.clear();
        setIsAuthenticated(false);
        window.location.href = 'https://login.microsoftonline.com/common/oauth2/logout';
    };

    const getUser = () => {
        setLoading(true);
        instance.handleRedirectPromise().then(authenticationResult => {
            if(authenticationResult) {
                setUser({
                    accessToken: authenticationResult.accessToken,
                    expiresOn: authenticationResult.expiresOn,
                    name: authenticationResult.account?.name ?? '',
                    roles: authenticationResult.idTokenClaims['roles'],
                    groups: authenticationResult.idTokenClaims['groups']
                })
                setLoading(false);
                setIsAuthenticated(true);
            } else {
                const account = instance.getAllAccounts()[0];
                const accessTokenRequest = {
                    scopes: [`api://${process.env.REACT_APP_AZURE_CLIENT_ID}/.default`],
                    account: account
                };
                instance.acquireTokenSilent(accessTokenRequest).then(accessTokenResponse => {
                    setUser({
                        accessToken: accessTokenResponse.accessToken,
                        expiresOn: accessTokenResponse.expiresOn,
                        name: accessTokenResponse.account?.name ?? '',
                        roles: accessTokenResponse.idTokenClaims['roles'],
                        groups: accessTokenResponse.idTokenClaims['groups'],
                    })
                    setLoading(false);
                    setIsAuthenticated(true);
                })
                .catch((e: any) => {
                    // console.log(e);
                    setUser(userDefault);
                    setLoading(false);
                });
            }
        }).catch((e: any) => {
            console.log(e);
            setUser(userDefault);
            setIsAuthenticated(false);
            setLoading(false);
        });
    };

    const login = () => {
        instance.loginRedirect({
            scopes: [`api://${process.env.REACT_APP_AZURE_CLIENT_ID}/.default`]
        });
    };

    const logout = () => {
        localStorage.removeItem('compensationVouchersHistoryObj');
        localStorage.removeItem('reasonMasterData');
        localStorage.setItem('logout-event', 'logout' + Math.random());
        setIsAuthenticated(false);
        instance.logoutRedirect({
            authority: `${process.env.REACT_APP_AZURE_AUTH_DOMAIN}${process.env.REACT_APP_AZURE_TENANT_ID}`
        });
    };

    window.addEventListener('storage', (event) => {
        if (event.key === 'logout-event') {
            localStorage.clear();
            window.location.href = 'https://login.microsoftonline.com/common/oauth2/logout';
        };
    });

    return (
        <AuthContext.Provider 
            value={{
                loading,
                user,
                acquireTokenSilently,
                forceLogout,
                getUser,
                login, 
                logout,
                isAuthenticated
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthProvider = () => {
    return useContext(AuthContext);
};